import { ClientOnly, createFileRoute, isNotFound, redirect } from '@tanstack/react-router';
import * as z from 'zod/mini';
import { preloadImageOnClient, heroImageSize, campaignImageSize } from '@worldcoo/ui/lib/utils';
import {
	portalAuthorOptions,
	portalMetadataOptions,
	portalCampaignsOptions,
	portalCampaignsAggregatesOptions,
	usePortalMetadataQuery,
} from '@/lib/queries-portal';
import { Campaigns } from './-components/campaigns';
import { Hero } from './-components/hero';
import { Footer } from './-components/footer';
import { Header } from './-components/header';
import { lazy, Suspense, useEffect } from 'react';
import { Seo } from './-components/seo';
import { useEphemeralStore } from '@/lib/app-store';
import { categoriesKeys } from '@worldcoo/shared/categories';
import { generateImageSrc, getLogoImageSize } from '@/lib/shared';
import { Sidebar, SidebarProvider } from '@worldcoo/ui/components/sidebar';
import { CampaignsFiltersSidebar } from './-components/campaigns-filters-sidebar';
import { isServer } from '@tanstack/react-query';
import { ensureSeoData } from '@/lib/ensure-seo-data';

const TestimonialSlider = lazy(() =>
	import('./-components/testimonial-slider').then((m) => ({ default: m.TestimonialSlider })),
);
const FaqSection = lazy(() => import('./-components/faq-section').then((m) => ({ default: m.FaqSection })));

export const Route = createFileRoute('/$portalId/')({
	validateSearch: z.object({
		q: z.catch(z.optional(z.string()), undefined),
		year: z.catch(z.optional(z.number()), undefined),
		state: z.catch(z.optional(z.literal('finished')), undefined),
		causes: z.catch(z.optional(z.array(z.enum(categoriesKeys))), undefined),
	}),
	loaderDeps: ({ search: { q, year, state, causes, city, iframe } }) => ({
		q,
		year,
		causes,
		state,
		city,
		iframe,
	}),
	loader: async ({ context, deps, params }) => {
		context.queryClient
			.ensureQueryData(portalAuthorOptions({ portalId: context.portalId, locale: context.locale }))
			.then((data) => {
				if ('notFound' in data) return;
				if (!data.logo?.location) return;
				preloadImageOnClient(
					generateImageSrc(data.logo.location, {
						height: getLogoImageSize(context.portalId).height,
						width: (data.logo.width / data.logo.height) * getLogoImageSize(context.portalId).height,
					}),
				);
			});

		await ensureSeoData(
			context,
			context.queryClient
				.ensureQueryData(portalMetadataOptions({ portalId: context.portalId, locale: context.locale }))
				.then((data) => {
					if ('notFound' in data) return;
					if (!data.media?.location) return;
					preloadImageOnClient(generateImageSrc(data.media.location, heroImageSize));
				}),
		);

		context.queryClient.ensureQueryData(portalCampaignsAggregatesOptions({ portalId: context.portalId }));

		const portalCampaignsPromise = context.queryClient
			.ensureQueryData(
				portalCampaignsOptions({
					portalId: context.portalId,
					locale: context.locale,
					query: deps.q,
					year: deps.year,
					state: deps.state,
					causes: deps.causes,
					city: deps.city,
				}),
			)
			.then((data) => {
				if ('notFound' in data) return;

				// If we're using standalone donations and we have city and only one campaign, redirect to it
				if (isServer && deps.iframe && deps.city && data.campaigns.length === 1) {
					throw redirect({
						to: '/$portalId/$campaignId',
						params: {
							portalId: params.portalId,
							campaignId: data.campaigns[0]!.id,
						},
						search: (prev) => prev,
					});
				}

				let count = 0;
				for (const campaign of data.campaigns) {
					if (!campaign.media?.location) continue;
					preloadImageOnClient(generateImageSrc(campaign.media.location, campaignImageSize));
					if (++count === 6) break;
				}
			});

		if (isServer && deps.iframe && deps.city) {
			await portalCampaignsPromise;
		}
	},
	component: Component,
});

function Component() {
	const pagesLoaded = useEphemeralStore((store) => store.pagesLoaded);
	const setPagesLoad = useEphemeralStore((store) => store.setPagesLoaded);
	useEffect(() => {
		if (pagesLoaded.includes('home')) return;
		const timeout = setTimeout(() => setPagesLoad([...pagesLoaded, 'home']), 1500);
		return () => clearTimeout(timeout);
	}, [pagesLoaded, setPagesLoad]);

	return (
		<SidebarProvider defaultOpen={false}>
			<Suspense>
				<SuspensedHead />
			</Suspense>

			<main className="flex w-full flex-col">
				<Header />
				<section className="mx-auto w-full max-w-[1366px]">
					<Hero />
				</section>

				<div id="campaigns" className="bg-secondary border-border flex-1 border-t">
					<main className="mx-auto flex max-w-[1366px] flex-col gap-6 px-6 py-16 lg:gap-12 lg:py-24">
						<Campaigns />
					</main>
				</div>

				<Suspense>
					<ConditionalTestimonialSlider />
				</Suspense>

				<Suspense>
					<ConditionalFaqSection />
				</Suspense>

				<Footer />
			</main>
			{/* Not needed for SEO, unload these from server to improve performance */}
			<ClientOnly>
				<Sidebar side="right">
					<CampaignsFiltersSidebar />
				</Sidebar>
			</ClientOnly>
		</SidebarProvider>
	);
}

function ConditionalTestimonialSlider() {
	const metadata = usePortalMetadataQuery();
	if (isNotFound(metadata)) return null;
	if (!metadata.sliderItems || metadata.sliderItems.length === 0) return null;
	return <TestimonialSlider />;
}

function ConditionalFaqSection() {
	const metadata = usePortalMetadataQuery();
	if (isNotFound(metadata)) return null;
	if (!metadata.faqItems || metadata.faqItems.length === 0) return null;
	return <FaqSection />;
}

function SuspensedHead() {
	const metadata = usePortalMetadataQuery();
	if (isNotFound(metadata)) return null;

	return (
		<Seo
			title={metadata.texts.title}
			description={metadata.texts.presentation}
			image={metadata.media?.location ? generateImageSrc(metadata.media.location) : undefined}
		/>
	);
}
